import { TutorReportsHelperService } from '../../_helpers/tutor-reports-helper/tutor-reports-helper.service';
import { Component, OnInit, OnChanges, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { MediaObserver } from '@ngbracket/ngx-layout';
import { Subscription } from 'rxjs';
import { SessionGroup, SessionTileParams } from '../../_models/session';
import { AuthenticationService, SessionService } from '../../_services/index';
import { Data } from '../../providers/data/data';
import { Router } from "@angular/router";
import { ClientDataHelper } from '../../_helpers/client-data-helper';

import { SuccessModalHelperService } from '../../_helpers/success-modal-helper/success-modal-helper.service';
import { ThemeService } from '../../_helpers/theming-engine/theme.service';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { FeatureHelperService } from '../../_helpers/feature-helper/feature-helper.service';
import { ColumnDef, StatusVariant } from '../../shared/data-table/data-table.model';
import { SelectedSessionDate } from '../../_models/student-bookings/selected-session-date.model';
@Component({
    selector: 'student-sessions-tile',
    templateUrl: 'sessions.tile.component.html',
    styleUrls: ['session.tile.component.css'],
    standalone: false
})

export class SessionTileComponent implements OnInit, OnChanges, OnDestroy {
  @Input() sessionTileParams: SessionTileParams;
  @Input() currDate: Date;
  @Output() dataLoaded: EventEmitter<any> = new EventEmitter<any>();

  public rows: any[] = [];
  public columns: ColumnDef[] = [];
  public noData: boolean = false;
  public searchData: string = 'sessionPageSearch';

  private staffFilter: boolean = false;
  private readOnly: boolean = false;
  private mediaSubscription: Subscription;

  constructor(
    private sessionService: SessionService,
    private router: Router,
    private data: Data,
    private clientDataHelper: ClientDataHelper,
    private successModalHelper: SuccessModalHelperService,
    private tutorReportsHelperService: TutorReportsHelperService,
    private authService: AuthenticationService,
    public themeService: ThemeService,
    private errorModalHelper: ErrorModalHelperService,
    private featureHelperService: FeatureHelperService,
    private media: MediaObserver
  ) { }

  ngOnInit() {

    let featureList = new Array<string>();
    featureList.push("StaffFilter");

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);

    this.staffFilter = currentUserFeatures.filter(f => f.feature === "StaffFilter")[0].enabled;

    this.readOnly = this.featureHelperService.getUserFeatures()
      .some(f => f.feature === 'TutorReportsView' && f.enabled) === false;

    this.buildColumns();
    this.mediaSubscription = this.media.asObservable().subscribe(() => this.buildColumns());
  }

  ngOnDestroy() {
    this.mediaSubscription?.unsubscribe();
  }

  private buildColumns(): void {
    const studentHeader = 'Learner/Group';
    // Reduced set is a viewport concern; the Tutor column alone stays login-scoped
    // (tutor logins only ever see their own sessions, so it is redundant for them).
    const mobileView = this.media.isActive('xs');
    const columns: ColumnDef[] = [];
    // The report's lifecycle status (New / Saved / Submitted / Approved / Rejected) — the reason a tutor
    // opens this tile after a rejection email. Rendered through app-session-status-pill so it shares the
    // calendar's colour/icon map, and never dropped: on mobile it leads the row.
    const statusColumn: ColumnDef = { field: 'status', header: 'Status', type: 'template', templateName: 'status', width: '1%' };
    if (mobileView) {
      columns.push(statusColumn);
    } else {
      columns.push({ field: 'startDate', header: 'Date', type: 'date', width: '12%' });
      // Kept to the left, next to the date, so online vs in-person reads at a glance instead of
      // sitting at the far right of the row. width 1% collapses the column to its natural minimum —
      // the <col> width is a suggestion the browser shrinks to fit, and the pill is white-space:
      // nowrap, so the widest value ("In person") still fits on one line. Dropped on mobile with
      // the other secondary columns.
      columns.push({ field: 'deliveryMode', header: 'Delivery', type: 'pill', width: '1%', pillMap: row => this.deliveryPill(row) });
      columns.push(statusColumn);
      if (!this.staffFilter) {
        columns.push({ field: 'tutorFullName', header: 'Tutor', width: '15%' });
      }
    }
    columns.push({ field: 'student', header: studentHeader, type: 'template', templateName: 'student', width: '15%' });
    if (!mobileView) {
      columns.push({ field: 'client', header: 'Client', width: '20%' });
    }
    columns.push({ field: 'time', header: 'Time', type: 'template', templateName: 'time' });
    columns.push({ field: 'subject', header: 'Subject' });
    if (!mobileView) {
      columns.push({ field: 'clientReferrer', header: 'Referrer' });
    }
    this.columns = columns;
  }

  /** Online bookings expect a Digital Classroom, so they get the stand-out variant. */
  private deliveryPill(row: SessionGroup): StatusVariant {
    return (row.deliveryMode || '').toLowerCase() === 'online' ? 'blue' : 'gray';
  }

  ngOnChanges() {
    if (this.sessionTileParams !== null) {
      if (this.sessionTileParams.preLoadedData != null) {
        this.populateDataSource(this.sessionTileParams.preLoadedData);
      } else {
        this.searchSession();
      }
    }
  }

  public onDataClicked(row): void {
    if (this.readOnly) {
      return;
    }

    if (this.sessionTileParams?.navigateToAddSession) {
      this.router.navigate(['/add-session-v2', row.placementID, row.sessionId], {
        state: {
          placementId: row.placementID,
          selectedSessionDate: new SelectedSessionDate(row.startDate, row.startTime, row.endTime),
          componentViewMode: 'Add'
        },
        queryParams: { returnUrl: window.location.pathname }
      });
      return;
    }

    this.data.storage = {
      startTime: row.startTime,
      endTime: row.endTime,
      placementId: row.placementID
    };

    this.tutorReportsHelperService.setCommonDataBeforeNavigate(row.placementID, row.startDate, row.endDate);
    setTimeout(() => {
      this.router.navigate(["/tutorreports", row.placementID]);
    }, 200);
  }

  private setOffsetDate(date): Date {
    var d = new Date(date);
    return new Date(d.setMinutes(d.getMinutes() - d.getTimezoneOffset()));
  }

  private searchSession(): void {
    // The slim LeapDb read (§7d): only the columns this tile renders, from the cached schedule.
    // `user` is deliberately not sent — the previous Orbit read accepted it and never applied it
    // (people-model, out of scope), so sending it here would imply scoping that does not happen.
    //
    // Bug 37026: `tutorIds` is no longer sent either. It used to be read from localStorage and
    // blanked for anyone without StaffFilter, which made the browser the authority on which tutor's
    // sessions you could see — and the server read an empty list as "every tutor", so the
    // least-privileged request returned the most data. The server now derives the tutor from the
    // database (NewBookingsService.ScopeTutorIdsToCaller) and intersects anything the client asks
    // for, so a value here could only ever narrow the result. Sending one anyway would restate a
    // rule that lives on the server and invite the two to drift apart again.
    var params = {
      startDate: this.setOffsetDate(this.sessionTileParams.startDate),
      endDate: this.setOffsetDate(this.sessionTileParams.endDate),
      tutorIds: [] as number[]
    };

    this.sessionService.getSessionTileRows(params).subscribe((data: any[]) => {
      if (data !== null) {
        this.populateDataSource(data);
      }
    }, error => {
        if (error.status === 401) {
          this.router.navigate(['/login']);
        }
        else {
          this.errorModalHelper.show(error.message, error);
        }
      }
    );
  }

  private populateDataSource(data: SessionGroup[]): void {
    if (data.length === 0) {
      this.noData = true;
      this.rows = [];
      this.dataLoaded.emit(false);
    } else {
      this.noData = false;
      var items = data;
      if (this.sessionTileParams.excludeToday) {
        items = items.filter(x => new Date(x.startDate).getDay() !== new Date().getDay());
      }

      this.rows = items;
    }
  }
}
